// FLOW Platform — Compound graph.
//
// The graph is built at plugin-build time from compound_words.xlsx with
// the spec §6 stage-1 filters applied:
//   - form_type == 'closed'
//   - zipf_w >= 1.5
//   - both part_a and part_b non-affix and non-apostrophe
//   - canonical_form is lowercase a-z only
//   - part_a + part_b == canonical_form (no misleading composition)
//
// At this writing: 4,733 edges, 2,290 nodes, 584 bridge-capable. ~50KB JSON,
// ~12KB gzipped — small enough to ship inline with the bundle.
//
// All keys/values are lowercase. Player input is lowercased before lookup.

import graph from './compound-graph.json';

const out_adj = graph.out_adj;
const open_pairs = graph.open || {};

/**
 * Is the edge a -> b an "open" (two-word) compound, like GOOD + MORNING
 * = "good morning"? Open pairs are listed in graph.open keyed "a|b".
 */
export function isOpenEdge(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  return Object.prototype.hasOwnProperty.call(
    open_pairs, a.toLowerCase() + '|' + b.toLowerCase()
  );
}

/**
 * Display form of the compound a+b. Open (two-word) compounds render with
 * a space ("good morning"); closed compounds glue ("lighthouse"). Used by
 * the lock toast and, since 2.14.26, the end screen's chain summary, so
 * two-word links read correctly in both.
 */
export function compoundDisplay(a, b) {
  const al = String(a || '').toLowerCase();
  const bl = String(b || '').toLowerCase();
  return isOpenEdge(al, bl) ? (al + ' ' + bl) : (al + bl);
}

/**
 * Does the directed edge a -> b exist? (i.e. is "a+b" a compound the
 * graph contains?)
 *
 * Both args are case-insensitive.
 */
export function hasEdge(a, b) {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  const al = a.toLowerCase();
  const list = out_adj[al];
  if (!list) return false;
  const bl = b.toLowerCase();
  // Linear scan — adjacency lists are tiny (mean ~2-3 entries, max ~50).
  return list.indexOf(bl) !== -1;
}

/**
 * Return the lowercase compound string formed by a + b, or null if
 * no such compound exists in the graph. Used for the toast that
 * announces a successful lock ("✓ WINDSCREEN").
 */
export function compoundOf(a, b) {
  if (!hasEdge(a, b)) return null;
  return (a + b).toLowerCase();
}

export const graphMeta = graph.meta;

// Every word that appears anywhere in the compound family — as a first
// half, a second half, or both. This is the vocabulary the real-word
// gate checks player guesses against: a guess outside this set earns
// no letter feedback at all (see game.js submit()).
const known_words = (() => {
  const s = new Set();
  for (const a in out_adj) {
    s.add(a);
    const list = out_adj[a];
    for (let i = 0; i < list.length; i++) s.add(list[i]);
  }
  return s;
})();

/**
 * Is this word part of the compound family at all? Case-insensitive.
 * Used by the real-word gate: guesses outside this vocabulary are
 * rejected without being recorded, so they never earn Wordle coloring
 * on the board or the keyboard.
 */
export function isKnownWord(w) {
  if (typeof w !== 'string') return false;
  return known_words.has(w.toLowerCase());
}

/**
 * Does any directed path of length `depth` exist from `from` to `to`?
 * Used by the hint system to find a valid bridge word given that future
 * slots also need to remain reachable to end_word.
 *
 *   depth = 1 → direct edge (same as hasEdge)
 *   depth = 2 → from -> mid -> to for some mid
 *   depth = 3 → from -> m1 -> m2 -> to for some m1, m2
 *
 * The graph has ~50 fan-out at the heaviest hubs and at most 4 steps
 * (par=3 + endpoint), so brute-force DFS is comfortably fast.
 */
export function hasPath(from, to, depth) {
  if (typeof from !== 'string' || typeof to !== 'string' || depth < 1) {
    return false;
  }
  const fromL = from.toLowerCase();
  const toL = to.toLowerCase();
  if (depth === 1) {
    return hasEdge(fromL, toL);
  }
  const next = out_adj[fromL];
  if (!next) return false;
  for (let i = 0; i < next.length; i++) {
    if (hasPath(next[i], toL, depth - 1)) return true;
  }
  return false;
}

/**
 * Return all valid words for the active slot, given the previous word
 * (locked or fixed start), the end word, and how many slots remain
 * after this one before the end (>=0).
 *
 *   slotsAfter = 0 → this slot's word must form edge to end_word directly
 *   slotsAfter = N → this slot's word must have a path of length N+1 to end_word
 *
 * Returns a lowercase array, sorted alphabetically.
 */
export function validBridgeWords(prev, endWord, slotsAfter) {
  const prevL = String(prev || '').toLowerCase();
  const endL = String(endWord || '').toLowerCase();
  const next = out_adj[prevL];
  if (!next) return [];
  const out = [];
  for (let i = 0; i < next.length; i++) {
    const w = next[i];
    if (slotsAfter === 0) {
      if (hasEdge(w, endL)) out.push(w);
    } else {
      // From w we need a path of (slotsAfter) edges to land on end_word.
      // That means an intermediate of slotsAfter-1 length then end edge,
      // i.e. hasPath(w, endL, slotsAfter + 1).
      if (hasPath(w, endL, slotsAfter + 1)) out.push(w);
    }
  }
  return out;
}
